import { FC, useState, useMemo } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Stack from "@mui/material/Stack";
import Accordion from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import AccordionDetails from "@mui/material/AccordionDetails";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CheckIcon from "@mui/icons-material/Check";
import { FILTERS_GROUP, FILTERS_KEYS } from "../../constants";
import { useFiltersContext } from "../../FiltersContext";
import { isFilterFilled } from "../../utils";
import { lightScrollbarStyle } from "../../../../../styles/lightScrollbarStyle";
import { useMatchDataProcessingStore } from "../../../../../store/MatchDataProcessingStore";
import { useWorkspaceStore } from "../../../../../store/workspace/WorkspaceStore";

interface FiltersGroupProps {
  activeFilterId: string;
  onFilterChange: (filterId: string) => void;
}

export const FiltersGroup: FC<FiltersGroupProps> = (props) => {
  const { draftFilters } = useFiltersContext();
  const persistedCompanyMatchId = useWorkspaceStore(
    (state) => state.userFilters?.companyMatch?.fileId
  );
  const workspaceId = useWorkspaceStore((state) => state.draft?.id);
  const hasCompanyMatchBeingProcessed = useMatchDataProcessingStore((state) =>
    workspaceId
      ? state.getProcessingFile(workspaceId, "uploadedAccounts")?.fileId
      : null
  );

  const filtersGroups = useMemo(
    () =>
      FILTERS_GROUP.map((group) => ({
        ...group,
        items: group.items.map((item) => {
          if (item.filterKey === FILTERS_KEYS.companyMatch) {
            return {
              ...item,
              isFilled:
                hasCompanyMatchBeingProcessed ||
                draftFilters?.companyMatch?.fileId ||
                persistedCompanyMatchId,
            };
          }

          return {
            ...item,
            isFilled: isFilterFilled(item.filterKey, draftFilters),
          };
        }),
      })),
    [draftFilters, hasCompanyMatchBeingProcessed, persistedCompanyMatchId]
  );

  const [expandedGroups, setExpandedGroups] = useState(
    filtersGroups.map((group) => group.id)
  );

  const handleGroupChange = (groupId: string) => {
    setExpandedGroups((prevExpandedGroups) => {
      if (prevExpandedGroups.includes(groupId)) {
        return prevExpandedGroups.filter((id) => id !== groupId);
      }
      return [...prevExpandedGroups, groupId];
    });
  };

  return (
    <Box
      sx={{
        borderRight: 1,
        borderColor: "divider",
        minWidth: 280,
        overflow: "auto",
        ...lightScrollbarStyle,
      }}
    >
      {filtersGroups.map((group, categoryIndex) => {
        const groupId = group.id;

        return (
          <Accordion
            key={groupId}
            expanded={expandedGroups.includes(groupId)}
            onChange={() => handleGroupChange(groupId)}
            disableGutters
            elevation={0}
            sx={{
              "&:before": { display: "none" },
              borderBottom:
                categoryIndex < filtersGroups.length - 1
                  ? "1px solid rgba(0, 0, 0, 0.12)"
                  : "none",
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              aria-controls={`${groupId}-content`}
              id={`${groupId}-header`}
              sx={{
                minHeight: 48,
                backgroundColor: "rgba(0, 0, 0, 0.03)",
                borderBottom: "1px solid rgba(0, 0, 0, 0.12)",
              }}
            >
              <Stack direction="row" alignItems="center">
                <Stack sx={{ mr: 1, color: "text.secondary" }}>
                  {group.icon}
                </Stack>
                <Typography
                  variant="overline"
                  color="text.secondary"
                  fontWeight={800}
                >
                  {group.title}
                </Typography>
              </Stack>
            </AccordionSummary>
            <AccordionDetails sx={{ padding: 0 }}>
              {group.items.map((filter) => {
                const filterId = filter.id;

                return (
                  <Box
                    key={filterId}
                    onClick={() => props.onFilterChange(filterId)}
                    sx={{
                      py: 1,
                      cursor: "pointer",
                      bgcolor:
                        props.activeFilterId === filterId
                          ? "rgba(30, 211, 235, 0.06)"
                          : "transparent",
                      "&:hover": {
                        bgcolor: "rgba(0, 0, 0, 0.08)",
                      },
                    }}
                  >
                    <Stack direction="row" alignItems="center" pl={3}>
                      <Stack
                        direction="row"
                        alignItems="center"
                        justifyContent="center"
                        width={16}
                      >
                        {filter.isFilled && (
                          <CheckIcon
                            color="primary"
                            fontSize="small"
                            aria-label="Filter has values"
                          />
                        )}
                      </Stack>
                      <Typography
                        variant="body1"
                        component="div"
                        color={
                          props.activeFilterId === filterId
                            ? "primary.main"
                            : "text.secondary"
                        }
                        ml={1}
                      >
                        {filter.label}
                      </Typography>
                    </Stack>
                  </Box>
                );
              })}
            </AccordionDetails>
          </Accordion>
        );
      })}
    </Box>
  );
};
